import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Video, Clock, Loader2, Play, Pause, Square, MoreVertical, Info, HelpCircle, Menu
} from 'lucide-react';

interface ControlToolbarProps {
  onBackToDashboard: () => void;
  status: 'idle' | 'initializing' | 'countdown' | 'recording' | 'paused' | 'completed';
  timer: number;
  formatTime: (secs: number) => string;
  addressBarUrl: string;
  setAddressBarUrl: (url: string) => void;
  handleNavigateAddressBar: (url: string) => void;
  handleStart: () => void;
  handlePause: () => void;
  handleResume: () => void;
  handleStop: () => void;
  countdownVal: number;
  configMenuOpen: boolean;
  setConfigMenuOpen: (open: boolean) => void;
  configMenuRef: React.RefObject<HTMLDivElement | null>;
  useMic: boolean;
  setUseMic: (use: boolean) => void;
  zoomEnabled: boolean;
  setZoomEnabled: (enabled: boolean) => void;
  spotlightEnabled: boolean;
  setSpotlightEnabled: (enabled: boolean) => void;
  keystrokesEnabled: boolean;
  setKeystrokesEnabled: (enabled: boolean) => void;
  forceLogoutOnActivation: boolean;
  setForceLogoutOnActivation: (logout: boolean) => void;
  onShowHelp: () => void;
  isFeedbackMode?: boolean;
}

export const ControlToolbar: React.FC<ControlToolbarProps> = ({
  onBackToDashboard,
  status,
  timer,
  formatTime,
  addressBarUrl,
  setAddressBarUrl,
  handleNavigateAddressBar,
  handleStart,
  handlePause,
  handleResume,
  handleStop,
  countdownVal,
  configMenuOpen,
  setConfigMenuOpen,
  configMenuRef,
  useMic,
  setUseMic,
  zoomEnabled,
  setZoomEnabled,
  spotlightEnabled,
  setSpotlightEnabled,
  keystrokesEnabled,
  setKeystrokesEnabled,
  forceLogoutOnActivation,
  setForceLogoutOnActivation,
  onShowHelp,
  isFeedbackMode
}) => {
  const [showHelpTooltip, setShowHelpTooltip] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowHelpTooltip(false);
    }, 8000);
    return () => clearTimeout(timer);
  }, []);

  if (isMobile) {
    return (
      <div style={{
        height: 56,
        display: status === 'recording' ? 'none' : 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 12px',
        borderBottom: '2px solid #7f1d1d',
        background: 'linear-gradient(180deg, #dc2626 0%, #991b1b 100%)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
        zIndex: 50,
        position: 'relative'
      }}>
        {/* Left Side: Exit & Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={onBackToDashboard}
            style={{ background: 'transparent', border: 'none', color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, fontSize: '.75rem', fontWeight: 'bold' }}
            title="Sair do Gravador"
          >
            <ArrowLeft size={14} style={{ color: '#ffffff' }} /> Sair
          </button>
          <div style={{ height: 14, width: 1, background: 'rgba(255, 255, 255, 0.3)' }} />
          <div style={{ fontWeight: 800, fontSize: '.75rem', color: '#ffffff', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Video size={14} /> duRecoder
          </div>
        </div>

        {/* Center/Right: Status, Timer and Main Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Status e Timer (compact) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 6px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: 12, border: '1px solid rgba(255, 255, 255, 0.2)' }}>
            <span style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: status === 'recording' ? '#ef4444' : status === 'paused' ? '#eab308' : (status === 'initializing' || status === 'countdown') ? '#f97316' : '#22c55e',
              animation: status === 'recording' ? 'notif-dot-pulse 1s infinite' : 'none'
            }} />
            <span style={{ fontSize: '.68rem', fontFamily: 'monospace', fontWeight: 600, color: '#ffffff' }}>
              {status === 'recording' ? formatTime(timer) : status === 'paused' ? formatTime(timer) : 'Pronto'}
            </span>
            <span style={{ color: 'rgba(255, 255, 255, 0.25)', fontSize: '.65rem', margin: '0 2px' }}>|</span>
            <span
              onClick={() => {
                if (status === 'idle') setUseMic(!useMic);
              }}
              title={useMic ? "Microfone Ativo (Voz será gravada)" : "Microfone Desativado (Apenas tela)"}
              style={{
                cursor: status === 'idle' ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                opacity: useMic ? 1 : 0.5,
                fontSize: '.62rem',
                userSelect: 'none'
              }}
            >
              {useMic ? '🎙️' : '🔇'}
            </span>
          </div>

          {/* Action Button */}
          {status === 'idle' && (
            <button
              onClick={handleStart}
              style={{
                height: 28,
                padding: '0 8px',
                borderRadius: 4,
                border: 'none',
                background: '#22c55e',
                color: '#fff',
                fontSize: '.7rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                boxShadow: '0 2px 4px rgba(34, 197, 94, 0.2)'
              }}
            >
              <Play size={12} fill="#fff" /> Iniciar
            </button>
          )}

          {(status === 'initializing' || status === 'countdown') && (
            <button
              disabled
              style={{
                height: 28,
                padding: '0 8px',
                borderRadius: 4,
                border: 'none',
                background: 'rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.6)',
                fontSize: '.7rem',
                fontWeight: 700,
                cursor: 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}
            >
              <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
              {status === 'initializing' ? 'Init...' : `${countdownVal}s`}
            </button>
          )}

          {status === 'recording' && (
            <>
              <button
                onClick={handlePause}
                style={{
                  height: 28,
                  padding: '0 6px',
                  borderRadius: 4,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  fontSize: '.7rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <Pause size={12} />
              </button>
              <button
                onClick={handleStop}
                style={{
                  height: 28,
                  padding: '0 8px',
                  borderRadius: 4,
                  border: 'none',
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: '.7rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <Square size={10} fill="#fff" /> Parar
              </button>
            </>
          )}

          {status === 'paused' && (
            <>
              <button
                onClick={handleResume}
                style={{
                  height: 28,
                  padding: '0 6px',
                  borderRadius: 4,
                  border: 'none',
                  background: '#ffffff',
                  color: '#991b1b',
                  fontSize: '.7rem',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                <Play size={12} fill="#991b1b" />
              </button>
              <button
                onClick={handleStop}
                style={{
                  height: 28,
                  padding: '0 8px',
                  borderRadius: 4,
                  border: 'none',
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: '.7rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <Square size={10} fill="#fff" /> Parar
              </button>
            </>
          )}

          {/* Hamburger Menu Trigger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ffffff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 6,
              marginLeft: 2
            }}
            className="hover:bg-white/10"
            title="Mais Opções"
          >
            <Menu size={18} />
          </button>
        </div>

        {/* Mobile Dropdown Menu Container */}
        {mobileMenuOpen && (
          <div style={{
            position: 'absolute',
            right: 8,
            top: 'calc(100% + 4px)',
            width: 260,
            background: '#0f172a',
            border: '1px solid #334155',
            borderRadius: 8,
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }}>
            {/* Address Bar Navigation (only in 'idle') */}
            {status === 'idle' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: '.7rem', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase' }}>
                  Navegar no Iframe
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="text"
                    value={addressBarUrl}
                    onChange={(e) => setAddressBarUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleNavigateAddressBar(addressBarUrl);
                        setMobileMenuOpen(false);
                      }
                    }}
                    placeholder="Ex: /admin/dashboard"
                    style={{
                      height: 32,
                      flex: 1,
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 6,
                      color: '#ffffff',
                      fontSize: '.75rem',
                      padding: '0 8px',
                      fontFamily: 'monospace'
                    }}
                  />
                  <button
                    onClick={() => {
                      handleNavigateAddressBar(addressBarUrl);
                      setMobileMenuOpen(false);
                    }}
                    style={{
                      height: 32,
                      padding: '0 10px',
                      background: '#ffffff',
                      color: '#991b1b',
                      fontSize: '.75rem',
                      fontWeight: 700,
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer'
                    }}
                  >
                    Ir
                  </button>
                </div>
              </div>
            )}

            {/* Captura Configuration switches */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: '.7rem', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', borderBottom: '1px solid #1e293b', paddingBottom: 4 }}>
                Configurações
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.75rem', color: '#f8fafc', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  disabled={status !== 'idle'}
                  checked={useMic}
                  onChange={(e) => setUseMic(e.target.checked)}
                />
                Capturar Voz (Mic)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.75rem', color: '#f8fafc', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={zoomEnabled}
                  onChange={(e) => setZoomEnabled(e.target.checked)}
                />
                Zoom (Shift)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.75rem', color: '#f8fafc', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={spotlightEnabled}
                  onChange={(e) => setSpotlightEnabled(e.target.checked)}
                />
                Holofote (Ctrl)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.75rem', color: '#f8fafc', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={keystrokesEnabled}
                  onChange={(e) => setKeystrokesEnabled(e.target.checked)}
                />
                Mostrar Teclas
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.75rem', color: '#f8fafc', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={forceLogoutOnActivation}
                  onChange={(e) => setForceLogoutOnActivation(e.target.checked)}
                />
                Auto-Deslogar ao Ativar
              </label>
            </div>

            {/* Manual & Shortcuts */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, borderTop: '1px solid #1e293b', paddingTop: 8 }}>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onShowHelp();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: '#7c3aed',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '6px 12px',
                  fontSize: '.75rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <HelpCircle size={14} /> Abrir Guia e Ajuda
              </button>

              <div style={{ fontSize: '.65rem', color: '#94a3b8', lineHeight: '1.4', marginTop: 4 }}>
                <div style={{ fontWeight: 700, color: '#f8fafc', marginBottom: 2 }}>Atalhos Rápidos:</div>
                • Ctrl+F9: Iniciar/Parar<br />
                • F10: Pausar/Continuar<br />
                • ESC: Parar Gravação<br />
                {isFeedbackMode && '• F4: Parar Gravação'}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      height: 56,
      display: status === 'recording' ? 'none' : 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 16px',
      borderBottom: '2px solid #7f1d1d',
      background: 'linear-gradient(180deg, #dc2626 0%, #991b1b 100%)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
      zIndex: 50
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={onBackToDashboard}
          style={{ background: 'transparent', border: 'none', color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '.85rem', fontWeight: 'bold' }}
          title="Sair do Gravador"
        >
          <ArrowLeft size={16} style={{ color: '#ffffff' }} /> Sair
        </button>
        <div style={{ height: 16, width: 1, background: 'rgba(255, 255, 255, 0.3)' }} />
        <div style={{ fontWeight: 800, fontSize: '.9rem', letterSpacing: '0.5px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Video size={16} /> duRecoder
        </div>
      </div>

      {/* Controles do Gravador */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        
        {/* Status e Timer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: 20, border: '1px solid rgba(255, 255, 255, 0.2)' }}>
          <span style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: status === 'recording' ? '#ef4444' : status === 'paused' ? '#eab308' : (status === 'initializing' || status === 'countdown') ? '#f97316' : '#22c55e',
            animation: status === 'recording' ? 'notif-dot-pulse 1s infinite' : 'none'
          }} />
          <span style={{ fontSize: '.78rem', fontFamily: 'monospace', fontWeight: 600, color: '#ffffff' }}>
            {status === 'recording' ? `Gravando ${formatTime(timer)}` : status === 'paused' ? `Pausado ${formatTime(timer)}` : status === 'initializing' ? 'Inicializando...' : status === 'countdown' ? 'Preparando...' : 'Pronto'}
          </span>
          <span style={{ color: 'rgba(255, 255, 255, 0.25)', fontSize: '.75rem', margin: '0 2px' }}>|</span>
          <span
            onClick={() => {
              if (status === 'idle') setUseMic(!useMic);
            }}
            title={useMic ? "Microfone Ativo (Voz será gravada)" : "Microfone Desativado (Apenas tela)"}
            style={{
              cursor: status === 'idle' ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              opacity: useMic ? 1 : 0.5,
              fontSize: '.85rem',
              userSelect: 'none'
            }}
          >
            {useMic ? '🎙️' : '🔇'}
          </span>
        </div>

        <div style={{ height: 16, width: 1, background: 'rgba(255, 255, 255, 0.3)' }} />

        {/* Botões Principais */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          
          {/* Barra de Endereço (somente no estado 'idle') */}
          {status === 'idle' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="text"
                  value={addressBarUrl}
                  onChange={(e) => setAddressBarUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleNavigateAddressBar(addressBarUrl);
                    }
                  }}
                  placeholder="Navegar para rota..."
                  style={{
                    height: 32,
                    width: 220,
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 6,
                    color: '#ffffff',
                    fontSize: '.75rem',
                    padding: '0 10px',
                    fontFamily: 'monospace'
                  }}
                />
                <button
                  onClick={() => handleNavigateAddressBar(addressBarUrl)}
                  style={{
                    height: 32,
                    padding: '0 10px',
                    background: '#ffffff',
                    color: '#991b1b',
                    fontSize: '.75rem',
                    fontWeight: 700,
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  Ir
                </button>
              </div>
              <div style={{ height: 16, width: 1, background: 'rgba(255, 255, 255, 0.3)' }} />
            </>
          )}

          {status === 'idle' && (
            <button
              onClick={handleStart}
              style={{
                height: 32,
                padding: '0 14px',
                borderRadius: 6,
                border: 'none',
                background: '#22c55e',
                color: '#fff',
                fontSize: '.78rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 2px 4px rgba(34, 197, 94, 0.2)'
              }}
            >
              <Play size={14} fill="#fff" /> Iniciar Gravação
            </button>
          )}

          {(status === 'initializing' || status === 'countdown') && (
            <button
              disabled
              style={{
                height: 32,
                padding: '0 14px',
                borderRadius: 6,
                border: 'none',
                background: 'rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.6)',
                fontSize: '.78rem',
                fontWeight: 700,
                cursor: 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              {status === 'initializing' ? 'Inicializando...' : `Gravando em ${countdownVal}...`}
            </button>
          )}

          {status === 'recording' && (
            <>
              <button
                onClick={handlePause}
                style={{
                  height: 32,
                  padding: '0 12px',
                  borderRadius: 6,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  fontSize: '.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                <Pause size={14} /> Pausar
              </button>
              <button
                onClick={handleStop}
                style={{
                  height: 32,
                  padding: '0 14px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: '.78rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)'
                }}
              >
                <Square size={12} fill="#fff" /> Parar Gravação
              </button>
            </>
          )}

          {status === 'paused' && (
            <>
              <button
                onClick={handleResume}
                style={{
                  height: 32,
                  padding: '0 12px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#ffffff',
                  color: '#991b1b',
                  fontSize: '.78rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                <Play size={14} fill="#991b1b" /> Continuar
              </button>
              <button
                onClick={handleStop}
                style={{
                  height: 32,
                  padding: '0 14px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: '.78rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)'
                }}
              >
                <Square size={12} fill="#fff" /> Parar Gravação
              </button>
            </>
          )}
        </div>
      </div>

      {/* Informações Extras & Switches */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderRight: '1px solid rgba(255, 255, 255, 0.3)', paddingRight: 16, position: 'relative' }} ref={configMenuRef}>
          {/* Botão de Ajuda Onboarding */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => {
                setShowHelpTooltip(false);
                onShowHelp();
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#ffffff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 6,
                transition: 'background 0.2s'
              }}
              className="hover:bg-white/10"
              title="Ajuda e Manual Onboarding"
            >
              <HelpCircle size={18} />
            </button>

            {showHelpTooltip && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 10px)',
                right: -60,
                width: 170,
                background: '#7c3aed',
                color: 'white',
                padding: '8px 12px',
                borderRadius: 8,
                fontSize: '0.7rem',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.4)',
                zIndex: 99999,
                animation: 'pulse 2s infinite',
                lineHeight: '1.3'
              }}>
                {/* Arrow pointing up */}
                <div style={{
                  position: 'absolute',
                  top: -6,
                  right: 68,
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderBottom: '6px solid #7c3aed'
                }} />
                Precisa de ajuda? Clique aqui para ver o manual de controle.
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowHelpTooltip(false);
                  }}
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 4,
                    cursor: 'pointer',
                    fontSize: '0.62rem',
                    opacity: 0.8,
                    fontWeight: 'bold',
                    padding: '2px'
                  }}
                >
                  ✕
                </div>
              </div>
            )}
          </div>
          
          <button
            onClick={() => setConfigMenuOpen(!configMenuOpen)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ffffff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 6,
              transition: 'background 0.2s'
            }}
            className="hover:bg-white/10"
            title="Configurações do Estúdio"
          >
            <MoreVertical size={18} />
          </button>

          {configMenuOpen && (
            <div style={{
              position: 'absolute',
              right: 0,
              top: 'calc(100% + 6px)',
              width: 220,
              background: '#0f172a',
              border: '1px solid #334155',
              borderRadius: 8,
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10
            }}>
              <div style={{ fontSize: '.7rem', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', borderBottom: '1px solid #1e293b', paddingBottom: 6, marginBottom: 2 }}>
                Opções de Captura
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.75rem', color: '#f8fafc', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  disabled={status !== 'idle'}
                  checked={useMic}
                  onChange={(e) => setUseMic(e.target.checked)}
                  style={{ cursor: status === 'idle' ? 'pointer' : 'not-allowed' }}
                />
                Capturar Voz (Mic)
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.75rem', color: '#f8fafc', cursor: 'pointer' }} title="Pressione SHIFT durante a gravação para dar zoom no cursor">
                <input
                  type="checkbox"
                  checked={zoomEnabled}
                  onChange={(e) => setZoomEnabled(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Zoom (Shift)
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.75rem', color: '#f8fafc', cursor: 'pointer' }} title="Pressione CTRL durante a gravação para destacar o cursor com um holofote de luz">
                <input
                  type="checkbox"
                  checked={spotlightEnabled}
                  onChange={(e) => setSpotlightEnabled(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Holofote (Ctrl)
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.75rem', color: '#f8fafc', cursor: 'pointer' }} title="Exibe na tela os atalhos de teclado que você pressionar">
                <input
                  type="checkbox"
                  checked={keystrokesEnabled}
                  onChange={(e) => setKeystrokesEnabled(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Mostrar Teclas
              </label>

              <div style={{ fontSize: '.7rem', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', borderBottom: '1px solid #1e293b', paddingBottom: 6, marginTop: 4, marginBottom: 2 }}>
                Fluxo de Simulação
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.75rem', color: '#f8fafc', cursor: 'pointer' }} title="Limpa a sessão do iframe ao navegar para páginas de ativação">
                <input
                  type="checkbox"
                  checked={forceLogoutOnActivation}
                  onChange={(e) => setForceLogoutOnActivation(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Auto-Deslogar ao Ativar
              </label>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '.72rem', color: '#ffffff' }}>
          <span><Info size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle', color: '#ffffff' }} /> Atalho: Ctrl + F9</span>
          <span style={{ color: 'rgba(255, 255, 255, 0.3)' }}>|</span>
          <span>F10: Pausar</span>
          <span style={{ color: 'rgba(255, 255, 255, 0.3)' }}>|</span>
          <span>{isFeedbackMode ? 'F4 / ESC: Parar' : 'ESC: Parar'}</span>
          <span style={{ color: 'rgba(255, 255, 255, 0.3)' }}>|</span>
          <span>Shift: Zoom</span>
          <span style={{ color: 'rgba(255, 255, 255, 0.3)' }}>|</span>
          <span>Ctrl: Holofote</span>
          {!isFeedbackMode && (
            <>
              <span style={{ color: 'rgba(255, 255, 255, 0.3)' }}>|</span>
              <span>F4: Janela Status</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
